import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Expand, Maximize2, Minus, Plus } from 'lucide-react';
import { clamp } from '../core/math';
import { useStudio } from '../store/studioStore';
import { useComposition } from './useComposition';
import type { TextFieldResult } from './useTextField';

/** Fullscreen on the whole document, so panels and canvas fill the screen together. */
async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    // Denied by the browser or unavailable in an embedded frame; nothing to recover.
  }
}

interface View {
  zoom: number;
  panX: number;
  panY: number;
  /** True until the user has zoomed or panned, so resizes keep re-fitting. */
  auto: boolean;
}

export function CanvasHost({ result }: { result: TextFieldResult }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>({ zoom: 1, panX: 0, panY: 0, auto: true });
  const [wrapSize, setWrapSize] = useState({ width: 0, height: 0 });
  const [spaceHeld, setSpaceHeld] = useState(false);

  const canvas = useStudio((s) => s.doc.canvas);
  const seed = useStudio((s) => s.doc.seed);
  const fieldView = useStudio((s) => s.fieldView);
  const composition = useComposition(canvasRef, result, spaceHeld);

  const fit = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const pad = 48;
    const zoom = Math.min((wrap.clientWidth - pad) / canvas.width, (wrap.clientHeight - pad) / canvas.height);
    setView({ zoom: clamp(zoom, 0.02, 8), panX: 0, panY: 0, auto: true });
  }, [canvas.width, canvas.height]);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => setWrapSize({ width: wrap.clientWidth, height: wrap.clientHeight }));
    ro.observe(wrap);
    setWrapSize({ width: wrap.clientWidth, height: wrap.clientHeight });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (view.auto) fit();
  }, [canvas.width, canvas.height, wrapSize.width, wrapSize.height, view.auto, fit]);

  /**
   * Hold space to see the bare text.
   *
   * Panning is on the middle button and on Alt-drag instead of on space, because the
   * before/after comparison is the more valuable thing to have under the biggest key —
   * it is what tells you whether an effect is actually earning its place.
   */
  useEffect(() => {
    const isTypingTarget = (t: EventTarget | null): boolean =>
      t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    const down = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        setSpaceHeld(true);
      }
      if (e.key === 'f' && !e.shiftKey) fit();
      if (e.key === 'F' && e.shiftKey) void toggleFullscreen();
    };
    const up = (e: KeyboardEvent): void => {
      if (e.code === 'Space') setSpaceHeld(false);
    };
    // A window that loses focus never delivers the keyup, and the canvas would be stuck
    // showing the bare text.
    const blur = (): void => setSpaceHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, [fit]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      setView((v) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const zoom = clamp(v.zoom * factor, 0.02, 16);
        const rect = wrap.getBoundingClientRect();
        const cx = e.clientX - rect.left - rect.width / 2 - v.panX;
        const cy = e.clientY - rect.top - rect.height / 2 - v.panY;
        const k = zoom / v.zoom;
        return { zoom, panX: v.panX - cx * (k - 1), panY: v.panY - cy * (k - 1), auto: false };
      });
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, []);

  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const zoomPct = Math.round(view.zoom * 100);

  return (
    <div className="relative flex h-full min-w-0 flex-1 flex-col bg-ink-950">
      <div
        ref={wrapRef}
        className="relative flex-1 overflow-hidden"
        onPointerDown={(e) => {
          if (e.button !== 1 && !e.altKey) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, y: e.clientY, panX: view.panX, panY: view.panY };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          setView((v) => ({ ...v, panX: d.panX + (e.clientX - d.x), panY: d.panY + (e.clientY - d.y), auto: false }));
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
      >
        <div
          className="absolute left-1/2 top-1/2"
          style={{ transform: `translate(-50%, -50%) translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})` }}
        >
          <canvas
            ref={canvasRef}
            className="block shadow-[0_20px_80px_rgba(0,0,0,0.65)] ring-1 ring-ink-800"
            style={{
              width: canvas.width,
              height: canvas.height,
              imageRendering: view.zoom >= 2 ? 'pixelated' : 'auto',
            }}
          />
        </div>

        {result.error && (
          <div className="absolute left-1/2 top-6 -translate-x-1/2 rounded-md border border-red-900/60 bg-red-950/80 px-3 py-2 text-[11px] text-red-200">
            {result.error}
          </div>
        )}

        {spaceHeld && (
          <div className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 rounded-md bg-ink-900/90 px-3 py-1.5 text-[11px] text-ink-200 ring-1 ring-ink-700">
            Vorher — nur Text und Hintergrund
          </div>
        )}
      </div>

      {/* ── Status bar ───────────────────────────────────────────────────── */}
      <div className="flex h-8 shrink-0 items-center gap-4 border-t border-ink-800 bg-ink-900 px-3 font-mono text-[10px] text-ink-400">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="ghost-button h-5 w-5 p-0"
            title="Verkleinern"
            onClick={() => setView((v) => ({ ...v, zoom: clamp(v.zoom / 1.25, 0.02, 16), auto: false }))}
          >
            <Minus size={11} />
          </button>
          <span className="w-11 text-center tabular-nums">{zoomPct}%</span>
          <button
            type="button"
            className="ghost-button h-5 w-5 p-0"
            title="Vergrössern"
            onClick={() => setView((v) => ({ ...v, zoom: clamp(v.zoom * 1.25, 0.02, 16), auto: false }))}
          >
            <Plus size={11} />
          </button>
          <button type="button" className="ghost-button h-5 w-5 p-0" title="Einpassen (F)" onClick={fit}>
            <Maximize2 size={11} />
          </button>
          <button
            type="button"
            className="ghost-button h-5 w-5 p-0"
            title="Vollbild (Shift+F)"
            onClick={() => void toggleFullscreen()}
          >
            <Expand size={11} />
          </button>
        </div>

        <span className="text-ink-500">
          {canvas.width}×{canvas.height}
        </span>

        {result.stats && (
          <span title="Layout / Rasterisierung / SDF">
            Feld {result.stats.totalMs.toFixed(0)} ms
            <span className="text-ink-600">
              {' '}
              ({result.stats.contours} Konturen, {result.stats.vertices.toLocaleString('de-CH')} Punkte)
            </span>
          </span>
        )}

        {composition.stats && (
          <>
            <span title="Simulation + Zeichnen">{composition.stats.drawMs.toFixed(1)} ms/Frame</span>
            {composition.stats.postMs > 0.05 && (
              <span title="Post-Processing-Kette">+{composition.stats.postMs.toFixed(1)} ms FX</span>
            )}
            {composition.stats.animating && <span>{composition.fps.toFixed(0)} fps</span>}
            {composition.stats.elements > 0 && (
              <span>{composition.stats.elements.toLocaleString('de-CH')} Elemente</span>
            )}
            {composition.quality < 0.99 && (
              <span className="text-amber-400" title="Vorschau-Budget reduziert — der Export rendert immer voll">
                Vorschau {Math.round(composition.quality * 100)} %
              </span>
            )}
          </>
        )}

        <span className="text-ink-600">Seed {seed}</span>
        {fieldView !== 'render' && <span className="text-accent-500">Inspektor: {fieldView}</span>}

        <span className="ml-auto text-ink-600">
          Leertaste = vorher · Alt/Mittelklick + Ziehen = verschieben · Scrollrad = Zoom · ? = Tasten
        </span>
      </div>
    </div>
  );
}
